import { Grid } from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import AsyncLoadingButton from "../../../components/asyncLoadingButton/AsyncLoadingButton";
import LabelledCheckbox from "../../../components/labelledCheckbox/LabelledCheckbox";
import dayjs, { ISO_DATE_FORMAT } from "../../../dayjs";
import type { AdminProductOptionResponse } from "../productsApi";
import PriceInput from "./PriceInput";
import PriceModeSelector, { PriceMode } from "./PriceModeSelector";
import DateRangeGroup from "./dateRangeGroup/DateRangeGroup";
import { daysOfWeek } from "./dateRangeGroup/DaysOfWeek";
import type { PriceListPriceValue } from "./updateOptionPrices";
import { useOptionPricesBatch } from "./useOptionPricesBatch";

interface OptionProps {
  readonly name: string;
  readonly price: PriceListPriceValue | undefined;
  readonly selected: boolean | undefined;
  onPriceChange: (price: PriceListPriceValue) => void;
  onSelectionChange: (selected: boolean) => void;
}

const Option: FC<OptionProps> = ({ name, price, selected, onPriceChange, onSelectionChange }) => (
  <Grid
    container
    size={12}
    spacing={1}
    data-testid="product-option"
    sx={{
      alignItems: "center",
    }}
  >
    <Grid size={7}>
      <LabelledCheckbox label={name} checked={selected ?? false} onChange={onSelectionChange} />
    </Grid>
    <Grid size={5}>
      <PriceInput label="Cena" fullWidth value={price ?? 0} disabled={!selected} onChange={(v) => onPriceChange(v)} />
    </Grid>
  </Grid>
);

const MultiOptionEdit = () => {
  const { options, productId } = useOutletContext<{
    productId: string;
    options: AdminProductOptionResponse[];
  }>();
  const { updatePrices } = useOptionPricesBatch(productId);
  const [optionPrices, setOptionPrices] = useState<Record<string, { price: PriceListPriceValue; selected: boolean }>>(
    {},
  );
  const [priceMode, setPriceMode] = useState(PriceMode.SELLING_PRICE);
  const [ranges, setRanges] = useState([
    {
      dates: {
        start: dayjs().startOf("month").format(ISO_DATE_FORMAT),
        end: dayjs().endOf("month").format(ISO_DATE_FORMAT),
      },
      daysOfWeek: daysOfWeek.map(() => true),
    },
  ]);
  const [everyOtherDay, setEveryOtherDay] = useState(false);

  const handleSubmit = async () => {
    const options = Object.entries(optionPrices)
      .filter(([, { selected }]) => selected)
      .map(([optionId, { price }]) => ({ optionId, value: price }));
    const requestBody = everyOtherDay
      ? {
          type: "EVERY_OTHER_DAY" as const,
          options,
          dateRanges: ranges.map((r) => r.dates),
        }
      : {
          type: "DAYS_OF_WEEK" as const,
          options,
          ranges: ranges.map(({ dates, daysOfWeek }) => ({
            dates,
            daysOfWeek: daysOfWeek.map((v, idx) => (v ? idx : null)).filter((v) => v !== null),
          })),
        };
    await updatePrices(priceMode, requestBody);
  };

  if (!options || options.length <= 0) {
    return null;
  }
  const editEnabled = Object.values(optionPrices).some((v) => v.selected);
  return (
    <Grid container spacing={2}>
      <Grid
        container
        size={4}
        spacing={1}
        sx={{
          alignContent: "center",
        }}
      >
        {options.map((o) => (
          <Option
            key={o.id}
            name={o.name}
            price={optionPrices[o.id]?.price ?? 0}
            selected={optionPrices[o.id]?.selected ?? false}
            onPriceChange={(v) =>
              setOptionPrices({
                ...optionPrices,
                [o.id]: { ...optionPrices[o.id], price: v },
              })
            }
            onSelectionChange={(v) =>
              setOptionPrices((prev) => {
                const optionPrice = prev[o.id];
                return {
                  ...optionPrices,
                  [o.id]: { ...optionPrice, selected: v, price: optionPrice?.price ?? 0 },
                };
              })
            }
          />
        ))}
      </Grid>
      <Grid
        container
        size={8}
        spacing={2}
        sx={{
          alignContent: "center",
        }}
      >
        <Grid size={12}>
          <PriceModeSelector value={priceMode} onChange={setPriceMode} />
        </Grid>
        <Grid size={12}>
          <LabelledCheckbox label="Co drugi dzień" checked={everyOtherDay} onChange={setEveryOtherDay} />
        </Grid>
        <Grid size={12}>
          <DateRangeGroup withDaysOfWeek={!everyOtherDay} ranges={ranges} onChange={setRanges} />
        </Grid>
      </Grid>
      <Grid
        size={12}
        sx={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <AsyncLoadingButton variant="outlined" onClick={handleSubmit} disabled={!editEnabled}>
          Aktualizuj cenniki
        </AsyncLoadingButton>
      </Grid>
    </Grid>
  );
};

export default MultiOptionEdit;
